import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { EditUserProfileForm } from 'renderer/features/users-page/schemas/users.schemas';
import {
  useEditUserProfileMutation,
  useGetUserDetailsQuery,
} from 'renderer/features/users-page/services/users.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';

interface EditUserProfileProps {
  userId: string;
}

const initialForm: EditUserProfileForm = {
  firstName: '',
  lastName: '',
  organization: '',
  email: '',
};

const EditUser = ({ userId }: EditUserProfileProps) => {
  const [form, setForm] = useState<EditUserProfileForm>(initialForm);
  const {
    data: details,
    isFetching: userDetailsFetching,
    error: userDetailsError,
  } = useGetUserDetailsQuery(userId);
  const [
    editUserProfile,
    {
      isSuccess: editSuccess,
      error: editError,
      isLoading: editLoading,
      data: editResult,
    },
  ] = useEditUserProfileMutation();

  useEffect(() => {
    if (details) {
      setForm({
        firstName: details.user.firstName,
        lastName: details.user.lastName,
        organization: details.user.organization ?? '',
        email: details.user.email,
      });
    }
  }, [details]);

  const isLoading = userDetailsFetching || editLoading;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = () => {
    editUserProfile({
      ...form,
      userId,
    });
  };

  return (
    <Box display="flex" justifyContent="left" mt={0}>
      <Paper elevation={0} sx={{ p: 2, width: '100%', maxWidth: 500 }}>
        {/* <Typography variant="h4" gutterBottom>
          Edit User Profile
        </Typography> */}

        <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 5 }}>
          Edit User Profile{' '}
        </Typography>

        {userDetailsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error fetching user details: {formatQueryError(userDetailsError)}
          </Alert>
        )}

        <Stack spacing={3}>
          <TextField
            size="small"
            label="First Name"
            name="firstName"
            disabled={isLoading}
            value={form.firstName}
            onChange={handleInputChange}
            // fullWidth
          />
          <TextField
            size="small"
            label="Last Name"
            name="lastName"
            disabled={isLoading}
            value={form.lastName}
            onChange={handleInputChange}
            fullWidth
          />
          <TextField
            size="small"
            label="Organization"
            name="organization"
            disabled={isLoading}
            value={form.organization}
            onChange={handleInputChange}
            fullWidth
          />
          <TextField
            size="small"
            label="Email"
            name="email"
            disabled={isLoading}
            value={form.email}
            onChange={handleInputChange}
            fullWidth
          />
        </Stack>

        {editError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            Error editing user profile: {formatQueryError(editError)}
          </Alert>
        )}

        {editSuccess && editResult && (
          <Alert severity="success" sx={{ mt: 3 }}>
            {editResult.message}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isLoading}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default EditUser;
